import { useEffect, useRef } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "hooks/useAuth";
import { shouldForceInteractiveReLogin } from "hooks/useMsalAppSession";
import { refreshMsalIdTokenCoalesced } from "services/msalTokenRefresh";
import {
  isCachedIdTokenExpired,
  isCustomAppSessionLifecycleValid,
} from "services/msalSessionRecovery";
import {
  isMsalInteractionRequiredError,
  isMsalSilentIframeTimeoutError,
  isMsalTokenExpirationError,
} from "utils/msalAuthErrors";
import { isNetworkFetchError } from "utils/networkErrors";
import { handleWakePhaseAuthFailure, resetWakePhaseAuthFailureGuard } from "utils/handleWakePhaseAuthFailure";
import { markWakeAuthPhase } from "utils/wakeAuthPhase";
import Helper from "utils/helper";
import { readMfaValidatedRaw } from "utils/mfaStorage";

/** Delay after resume so Wi‑Fi / network stack can reconnect before MSAL calls. */
const WAKE_NETWORK_STABILIZE_MS = 2500;

/**
 * Ignore brief tab switches; only treat longer hidden periods as sleep / idle wake.
 * Keeps normal active sessions from redundant silent refresh round-trips.
 */
const MIN_HIDDEN_BEFORE_WAKE_MS = 60_000;

/** Align with axios proactive refresh window in setupAxiosBearer.js. */
const PROACTIVE_REFRESH_SEC = 300;

/**
 * @param {string | null | undefined} token
 * @returns {boolean}
 */
function isTokenNearExpiry(token) {
  if (!token) {
    return true;
  }
  try {
    const { exp } = jwtDecode(token);
    return (
      typeof exp !== "number" ||
      exp * 1000 < Date.now() + PROACTIVE_REFRESH_SEC * 1000
    );
  } catch {
    return true;
  }
}

/**
 * @returns {boolean}
 */
function hasPersistedAppSession() {
  return Helper.isLoggedIn() || readMfaValidatedRaw();
}

/**
 * Wake-up guard: after the device resumes from sleep (or long idle), wait for
 * network stabilization and re-verify auth via the existing coalesced silent
 * refresh path. Does not add timers or duplicate axios / lifecycle refresh logic.
 *
 * Network failures during this window (e.g. "Failed to fetch" while Wi‑Fi
 * reconnects) are caught and routed to interactive login instead of bubbling
 * to the global unhandled-rejection handler.
 *
 * @param {{ msalStartupPending?: boolean }} [options]
 */
export function useMsalSessionWakeGuard({ msalStartupPending = false } = {}) {
  const { instance, inProgress } = useMsal();
  const account = useAccount();
  const { signinHandler, isLoginRedirectPending } = useAuth();

  const hiddenAtRef = useRef(null);
  const wakeTimerRef = useRef(null);
  const wakeCheckInFlightRef = useRef(false);
  const interactiveFallbackTriggeredRef = useRef(false);

  useEffect(() => {
    if (account) {
      interactiveFallbackTriggeredRef.current = false;
      resetWakePhaseAuthFailureGuard();
    }
  }, [account]);

  useEffect(() => {
    const clearWakeTimer = () => {
      if (wakeTimerRef.current != null) {
        window.clearTimeout(wakeTimerRef.current);
        wakeTimerRef.current = null;
      }
    };

    const shouldSkipWakeCheck = () => {
      if (!hasPersistedAppSession()) {
        return true;
      }
      if (msalStartupPending || inProgress !== InteractionStatus.None) {
        return true;
      }
      if (isLoginRedirectPending || interactiveFallbackTriggeredRef.current) {
        return true;
      }

      const hiddenAt = hiddenAtRef.current;
      if (hiddenAt == null) {
        return true;
      }

      const hiddenDurationMs = Date.now() - hiddenAt;
      const token = localStorage.getItem("msal.idtoken");
      const tokenExpired = isCachedIdTokenExpired(token);
      const tokenNearExpiry = isTokenNearExpiry(token);

      // Short visibility blips with a healthy token are handled by existing interceptors.
      return (
        hiddenDurationMs < MIN_HIDDEN_BEFORE_WAKE_MS &&
        !tokenExpired &&
        !tokenNearExpiry
      );
    };

    const triggerInteractiveLogin = () => {
      if (interactiveFallbackTriggeredRef.current || isLoginRedirectPending) {
        return;
      }
      interactiveFallbackTriggeredRef.current = true;
      signinHandler();
    };

    const handleWakeFailure = (error) => {
      if (isMsalSilentIframeTimeoutError(error)) {
        if (isCustomAppSessionLifecycleValid()) {
          console.warn(
            "Wake guard: MSAL silent iframe timed_out (tracking prevention); custom session lifecycle still valid — skipping logout.",
            error,
          );
          return;
        }
      }

      console.warn("Wake guard: auth verification failed:", error);

      if (handleWakePhaseAuthFailure(error, triggerInteractiveLogin)) {
        interactiveFallbackTriggeredRef.current = true;
        return;
      }

      if (
        isNetworkFetchError(error) ||
        isMsalInteractionRequiredError(error) ||
        isMsalTokenExpirationError(error)
      ) {
        triggerInteractiveLogin();
        return;
      }

      if (shouldForceInteractiveReLogin(instance)) {
        triggerInteractiveLogin();
      }
    };

    const performWakeAuthCheck = async () => {
      if (wakeCheckInFlightRef.current || shouldSkipWakeCheck()) {
        return;
      }

      wakeCheckInFlightRef.current = true;
      try {
        // Reuse the same coalesced silent refresh used by axios interceptors and 401 retry.
        await refreshMsalIdTokenCoalesced();
      } catch (error) {
        handleWakeFailure(error);
      } finally {
        wakeCheckInFlightRef.current = false;
      }
    };

    const scheduleWakeAuthCheck = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      if (!hasPersistedAppSession() || hiddenAtRef.current == null) {
        return;
      }

      // Cover stabilize delay + token refresh + parallel SDK init (Sendbird, etc.).
      markWakeAuthPhase(WAKE_NETWORK_STABILIZE_MS);

      clearWakeTimer();
      wakeTimerRef.current = window.setTimeout(() => {
        wakeTimerRef.current = null;
        void performWakeAuthCheck().catch((error) => {
          handleWakeFailure(error);
        });
      }, WAKE_NETWORK_STABILIZE_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        clearWakeTimer();
        return;
      }
      scheduleWakeAuthCheck();
    };

    const onWindowFocus = () => {
      if (document.visibilityState === "visible") {
        scheduleWakeAuthCheck();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onWindowFocus);
      clearWakeTimer();
    };
  }, [
    instance,
    inProgress,
    msalStartupPending,
    signinHandler,
    isLoginRedirectPending,
  ]);
}
